import { PhotoIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useState, useEffect } from 'react'
import Button from '../Button'
import Modal from '../Modal'

type Props = {
    open: boolean
    setOpen: (open: boolean) => void
    imageUrl: string | null
    contentId: number | null
}

export default function ScreenshotImageModal({ open, setOpen, imageUrl, contentId }: Props) {
    const [imageError, setImageError] = useState(false)

    // Reset error state when modal opens with new URL
    useEffect(() => {
        if (open) {
            setImageError(false)
        }
    }, [open, imageUrl])

    if (!imageUrl) return null

    return (
        <Modal
            open={open}
            onClose={() => setOpen(false)}
            title={`Screenshot for content ${contentId}`}
            icon={<PhotoIcon className="h-8 w-8 text-ink-700" aria-hidden="true" />}
            size="2xl"
            footer={
                <>
                    <Button text="Close" primary={false} onClick={() => setOpen(false)} />
                    {!imageError && (
                        <a
                            href={imageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-arwen-600 hover:bg-arwen-700"
                        >
                            Open in new tab
                        </a>
                    )}
                </>
            }
        >
            <div className="flex justify-center">
                {imageError ? (
                    <div className="flex flex-col items-center justify-center py-12 text-ink-500">
                        <ExclamationTriangleIcon className="h-16 w-16 text-ink-400 mb-4" />
                        <p className="text-lg font-medium">Image not available</p>
                        <p className="text-sm mt-1">The screenshot image could not be loaded.</p>
                        <p className="text-xs mt-2 text-ink-400">The presigned URL may have expired.</p>
                    </div>
                ) : (
                    <img
                        src={imageUrl}
                        alt={`Screenshot for content ${contentId}`}
                        className="max-w-full max-h-[70vh] object-contain border border-ink-200 rounded"
                        onError={() => setImageError(true)}
                    />
                )}
            </div>
        </Modal>
    )
}
